/**
 * API Client pour Architecture Versioning
 *
 * Endpoints:
 * - Créer versions
 * - Historique et comparaison
 * - Rollback
 * - Tags
 */

import api from './client';

export interface ArchitectureVersion {
  id: string;
  architecture_id: string;
  version_number: number;
  terraform_code: {
    main: string;
    variables?: string;
    outputs?: string;
    [key: string]: string | undefined;
  };
  variables?: Record<string, any>;
  commit_message: string;
  author_id: string;
  author_name?: string;
  parent_version_id?: string;
  content_hash: string;
  is_tagged: boolean;
  tag_name?: string;
  tag_description?: string;
  created_at: string;
  children_count?: number;
}

export interface VersionComparison {
  from_version: ArchitectureVersion;
  to_version: ArchitectureVersion;
  changes: {
    added: number;
    removed: number;
    modified: number;
  };
  diff?: string;
}

export interface VersionTree {
  version: ArchitectureVersion;
  children: VersionTree[];
}

export interface CreateVersionRequest {
  terraform_code: {
    main: string;
    variables?: string;
    outputs?: string;
    [key: string]: string | undefined;
  };
  variables?: Record<string, any>;
  commit_message: string;
  parent_version_id?: string;
  diagram_data?: {
    nodes: any[];
    edges: any[];
  };
}

export interface TagVersionRequest {
  tag_name: string;
  description?: string;
}

/**
 * Créer une nouvelle version
 */
export const createVersion = async (
  architectureId: string,
  data: CreateVersionRequest
): Promise<ArchitectureVersion> => {
  const response = await api.post(`/api/architectures/${architectureId}/versions`, data);
  return response.data.version;
};

/**
 * Obtenir l'historique des versions
 */
export const getVersionHistory = async (
  architectureId: string,
  params?: { limit?: number; offset?: number }
): Promise<ArchitectureVersion[]> => {
  const response = await api.get(`/api/architectures/${architectureId}/versions`, { params });
  // L'API retourne { success, versions, pagination, ... }
  return response.data.versions || [];
};

/**
 * Obtenir une version spécifique
 */
export const getVersion = async (
  architectureId: string,
  versionId: string
): Promise<ArchitectureVersion> => {
  const response = await api.get(`/api/architectures/${architectureId}/versions/${versionId}`);
  return response.data.version;
};

/**
 * Comparer deux versions
 */
export const compareVersions = async (
  architectureId: string,
  fromVersionId: string,
  toVersionId: string
): Promise<VersionComparison> => {
  const response = await api.get(`/api/architectures/${architectureId}/versions/compare`, {
    params: {
      from: fromVersionId,
      to: toVersionId
    }
  });
  return response.data.comparison;
};

/**
 * Rollback vers une version précédente
 */
export const rollbackToVersion = async (
  architectureId: string,
  versionId: string,
  reason: string
): Promise<ArchitectureVersion> => {
  const response = await api.post(`/api/architectures/${architectureId}/versions/${versionId}/rollback`, {
    reason
  });
  return response.data.newVersion;
};

/**
 * Obtenir l'arbre des versions (parent-child)
 */
export const getVersionTree = async (
  architectureId: string,
  versionId: string
): Promise<VersionTree> => {
  const response = await api.get(`/api/architectures/${architectureId}/versions/${versionId}/tree`);
  return response.data;
};

/**
 * Obtenir la dernière version
 */
export const getLatestVersion = async (architectureId: string): Promise<ArchitectureVersion> => {
  const response = await api.get(`/api/architectures/${architectureId}/versions/latest`);
  return response.data.version;
};

/**
 * Obtenir le diff depuis le parent
 */
export const getVersionDiff = async (
  architectureId: string,
  versionId: string
): Promise<{
  diff: string;
  parent_version: ArchitectureVersion;
  current_version: ArchitectureVersion;
}> => {
  const response = await api.get(`/api/architectures/${architectureId}/versions/${versionId}/diff`);
  return response.data;
};

/**
 * Taguer une version
 */
export const tagVersion = async (
  architectureId: string,
  versionId: string,
  data: TagVersionRequest
): Promise<{ message: string }> => {
  const response = await api.post(`/api/architectures/${architectureId}/versions/${versionId}/tag`, data);
  return response.data;
};

/**
 * Lister les tags
 */
export const listTags = async (architectureId: string): Promise<ArchitectureVersion[]> => {
  const response = await api.get(`/api/architectures/${architectureId}/versions/tags`);
  return response.data;
};

export default {
  createVersion,
  getVersionHistory,
  getVersion,
  compareVersions,
  rollbackToVersion,
  getVersionTree,
  getLatestVersion,
  getVersionDiff,
  tagVersion,
  listTags
};
